import { useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { countryToISO, Skeleton, withStyles } from 'vopay';
import { BodyText } from 'UI';
import { useBlockingLoader } from 'shared';
import { Permissions, PermissionValues } from 'shared/permissions';
import usePartnerAccountPermissions from '@/components/Entities/hooks/useApi/usePartnerAccountPermissions';
import { usePartnerBearerLookup } from '@/components/Entities/hooks/useApi/usePartnerBearer';
import usePermissionLabels from '@/hooks/usePermissionLabels';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { usePartnerData } from '@/providers/PartnerData.provider';
import { useToast } from '@/providers/Toast.provider';
import AccountContext from '@/components/Entities/Accounts/AccountContext';
import adaptSubAccountPermissions from './adaptSubAccountPermissions';
import styles from './PermissionsTab.module.scss';

const cn = withStyles(styles);

/**
 * The account page's Permissions tab (spec 5.6.2, ID-13428): the partner portal's host for the shared
 * permissions editor. It fetches the account's nested permissions tree through the partner route as the
 * owning partner, adapts and label-bakes it, lays the shared parts out per the design — the search with
 * Enable All / Save in a toolbar above the full-width tree (ID-13981) — and saves. A Viewer gets the
 * tree read-only.
 */
export default function PermissionsTab() {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.Account.Permissions' });
  const { t: tTabs } = useTranslation('translations', { keyPrefix: 'Accounts.Account.Tabs' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const { accountId } = useParams<{ accountId: string }>();
  const { details } = useContext(AccountContext);
  const account = details[accountId];

  const { bearer: partnerBearer, hasSettled: hasBearerSettled } = usePartnerBearerLookup(
    account?.PartnerAccountID ?? '',
  );

  const {
    response: { isRoleViewOnly },
  } = usePartnerData();

  const { fetchingPermissions, permissions, fetchPartnerAccountPermissions, savePartnerAccountPermissions } =
    usePartnerAccountPermissions();

  const { resolvePermissionLabel, resolveSectionLabel } = usePermissionLabels();
  const alertDialog = useAlertDialog();
  const blockingLoader = useBlockingLoader();
  const toast = useToast();

  // Whether the first fetch has answered. The skeleton is gated on this rather than on `fetching`: the
  // refetch after a save must leave the tree (and every section the user expanded) in place, and the
  // first paint — before the fetch effect has run — must not flash an empty form.
  const [hasLoaded, setHasLoaded] = useState(false);

  const sections = useMemo(
    () =>
      adaptSubAccountPermissions(permissions, {
        resolveItemLabel: resolvePermissionLabel,
        resolveSectionLabel: (sectionID) => resolveSectionLabel(sectionID),
      }),
    [permissions, resolvePermissionLabel, resolveSectionLabel],
  );

  // A `Success: false` answer can carry an empty `ErrorMessage`; the dialog then says what failed.
  const describeError = (error: unknown, fallback: string) =>
    error instanceof Error && error.message ? error.message : fallback;

  const loadPermissions = async () => {
    try {
      await fetchPartnerAccountPermissions({ VopayAccountID: accountId }, partnerBearer);
    } catch (error) {
      // The hook has logged the cause; the tab shows the empty tree and the user sees why.
      alertDialog.error(
        t('Errors.title', { defaultValue: 'Error' }),
        describeError(error, t('Tab.loadError', { defaultValue: "Failed to load the account's permissions." })),
      );
    } finally {
      setHasLoaded(true);
    }
  };

  useEffect(() => {
    // The owning partner's bearer resolves asynchronously when it is not the logged-in partner (empty
    // until then), and the route authenticates as that partner — so the fetch waits for it.
    if (!partnerBearer) return;

    loadPermissions();
  }, [accountId, partnerBearer]);

  const handleSave = async (values: PermissionValues) => {
    blockingLoader.show();

    try {
      await savePartnerAccountPermissions({ VopayAccountID: accountId, ...values }, partnerBearer);
      toast.success(t('Tab.saved', { defaultValue: 'Permissions updated successfully.' }));
      // Reseeds the editor on what the backend now holds, so Save disables again until the next edit.
      await loadPermissions();
    } catch (error) {
      alertDialog.error(
        t('Errors.title', { defaultValue: 'Error' }),
        describeError(error, t('Tab.saveError', { defaultValue: "Failed to save the account's permissions." })),
      );
    } finally {
      blockingLoader.hide();
    }
  };

  // The listing sends the country as its display name; a US account's amounts are USD, everything else CAD.
  const currency = countryToISO(account?.address.country ?? '') === 'US' ? 'USD' : 'CAD';

  // The lookup has answered and there is no partner token to authenticate with — see TransactionsTab.
  if (hasBearerSettled && !partnerBearer) {
    return (
      <BodyText data-testid="permissionsTabUnavailable">
        {tTabs('unavailable', { defaultValue: "This account's data could not be loaded." })}
      </BodyText>
    );
  }

  return (
    <Permissions
      sections={sections}
      currency={currency}
      readOnly={isRoleViewOnly}
      formID="accountPermissionsForm"
      onSave={handleSave}
    >
      <div className={cn('tab')} data-testid="permissionsTab">
        {/* Figma: the search fills the row; `Buttons` Size="Standard", Enable All Type="Secondary", Save Type="Primary" */}
        <div className={cn('toolbar')} data-testid="permissionsToolbar">
          <div className={cn('search')}>
            <Permissions.Search />
          </div>

          <div className={cn('actions')}>
            <Permissions.EnableAllButton disabled={fetchingPermissions} />
            {/* The design's button reads "Save", not the shared default "Save Permissions". */}
            <Permissions.SaveButton disabled={fetchingPermissions}>{tGlobal('Buttons.save')}</Permissions.SaveButton>
          </div>
        </div>

        {hasLoaded ? <Permissions.Sections /> : <Skeleton height={320} data-testid="permissionsTabLoading" />}
      </div>
    </Permissions>
  );
}
